import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { VECTORE_API_BASE_URL } from '../constant/app-config';
import { map, Observable, shareReplay, switchMap, tap } from 'rxjs';
import { CommonService } from './common.service';
import {
  VehicleUsageDataResponse,
  VehicleUsageResponse,
} from '../models/vehicle-usages/vehicle-usages-response';
import { VehicleUsageRequest } from '../models/vehicle-usages/vehicle-usage-request';
import {
  TopVehicleDirversModel,
  TopVehicleUsagesModel,
  VehicleUsage,
} from '../models/vehicle-usages/vehicle-usages';
import { MappingModel } from '../models/user/users';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import {
  VehicleDataUsageFilter,
  VehicleUsageFilter,
  VehicleUsagesFilter,
} from '../models/vehicle-usages/vehicle-usage-filter';
import { TenantService } from './tenant.service';

@Injectable({
  providedIn: 'root',
})
export class VehicleUsagesService {
  private _tenantId: string;

  constructor(
    private _httpClient: HttpClient,
    private _tenantService: TenantService,
    private _commonService: CommonService,
    private _toasterService: ToastrService,
    private _translateService: TranslateService
  ) {
    this._tenantId = this._tenantService.getTenantId();
  }

  createVehicleUsage$(
    tenantId: string,
    vehicleUsage: VehicleUsageRequest
  ): Observable<VehicleUsage> {
    return this._httpClient
      .post<VehicleUsage>(
        `${VECTORE_API_BASE_URL}/${tenantId}/vehicle-usages`,
        vehicleUsage
      )
      .pipe(
        tap((response) => {
          if (response.id) {
            this._toasterService.success(
              this._translateService.instant(
                'BOOKINGS.MESSAGES.BOOKING_USAGE_CREATED'
              )
            );
          }
        }),
        tap({
          error: (error) => {
            this._commonService.handleError(error, false);
            this._toasterService.error(
              this._translateService.instant(
                `BOOKINGS.GLOBAL_EXCEPTION.${error.error.errorCode}`
              )
            );
          },
        })
      );
  }

  listVehicleUsages$(
    params?: VehicleUsagesFilter
  ): Observable<VehicleUsageResponse> {
    return this._httpClient.get<VehicleUsageResponse>(
      `${VECTORE_API_BASE_URL}/${this._tenantId}/vehicle-usages`,
      { params: this._commonService.buildParams(params) }
    );
  }

  getVehicleUsage$(
    vehicleUsageId: string,
    params?: VehicleUsageFilter
  ): Observable<VehicleUsage> {
    return this._httpClient
      .get<VehicleUsage>(
        `${VECTORE_API_BASE_URL}/${this._tenantId}/vehicle-usages/${vehicleUsageId}`,
        { params: this._commonService.buildParams(params) }
      )
      .pipe(shareReplay(1));
  }
  getTopVehicleUsages$(
    params?: VehicleDataUsageFilter
  ): Observable<TopVehicleUsagesModel> {
    params.includeBookingsCount = true;
    params.includeUsagesInProgress = true;
    return this._httpClient
      .get<VehicleUsageDataResponse>(
        `${VECTORE_API_BASE_URL}/${this._tenantId}/vehicle-usage-data`,
        { params: this._commonService.buildParams(params) }
      )
      .pipe(
        shareReplay(1),
        map((res) => {
          // Calculate total items across all data entries
          const totalItems = res.items.reduce(
            (sum, item) => sum + item.items,
            0
          );

          // Calculate total bookings across all data entries
          const totalBookings = res.items.reduce(
            (sum, item) => sum + item.itemsWithBookings,
            0
          );

          // Calculate total in progress across all data entries
          const totalInProgress = res.items.reduce(
            (sum, item) => sum + item.itemsInProgress,
            0
          ); // Calculate percentage of items that have bookings
          const fromBookingPercentage =
            totalItems > 0
              ? Math.round((totalBookings / totalItems) * 100 * 100) / 100
              : 0;

          return {
            total: totalItems,
            fromBooking: fromBookingPercentage,
            inProgress: totalInProgress,
          } as TopVehicleUsagesModel;
        })
      );
  }

  getTopVehicleDrivers$(
    params?: VehicleDataUsageFilter
  ): Observable<TopVehicleDirversModel[]> {
    params.includeUser = true;
    params.groupByUserId = '*';
    return this._httpClient
      .get<VehicleUsageDataResponse>(
        `${VECTORE_API_BASE_URL}/${this._tenantId}/vehicle-usage-data`,
        { params: this._commonService.buildParams(params) }
      )
      .pipe(
        shareReplay(1),
        map((res) => {
          return res.items
            .sort((a, b) => b.items - a.items)
            .slice(0, 4)
            .map(
              (r) =>
                ({
                  total: r.items,
                  driver: r.user?.displayName,
                } as TopVehicleDirversModel)
            );
        })
      );
  }

  updateVehicleUsage$(
    tenantId: string,
    vehicleUsageId: string,
    vehicleUsage: VehicleUsageRequest
  ): Observable<VehicleUsage> {
    return this._httpClient
      .patch<VehicleUsage>(
        `${VECTORE_API_BASE_URL}/${tenantId}/vehicle-usages/${vehicleUsageId}`,
        vehicleUsage
      )
      .pipe(
        tap((response) => {
          if (response.id) {
            this._toasterService.success(
              this._translateService.instant(
                'BOOKINGS.MESSAGES.BOOKING_USAGE_UPDATED'
              )
            );
          }
        }),
        tap({
          error: (error) => {
            this._commonService.handleError(error, false);
            this._toasterService.error(
              this._translateService.instant(
                `BOOKINGS.GLOBAL_EXCEPTION.${error.error.errorCode}`
              )
            );
          },
        })
      );
  }

  deleteVehicleUsage$(
    tenantId: string,
    vehicleUsageId: string
  ): Observable<VehicleUsage> {
    return this._httpClient.delete<VehicleUsage>(
      `${VECTORE_API_BASE_URL}/${tenantId}/vehicle-usages/${vehicleUsageId}`
    );
  }

  exportVehicleUsages$(
    tenantId: string,
    exportData: MappingModel,
    params?: any
  ): Observable<HttpResponse<Blob>> {
    return this._httpClient
      .post(
        `${VECTORE_API_BASE_URL}/${tenantId}/export/vehicle-usages`,
        exportData,
        {
          observe: 'response',
          responseType: 'blob',
          params: this._commonService.buildParams(params),
        }
      )
      .pipe(
        tap({
          next: (response) => {
            const now = new Date();
            const filename = `export_vehicle_usages_${now.toLocaleDateString(
              'it-IT'
            )}.xlsx`;
            this._commonService.downloadFile(response, filename);
          },
          error: (error) => this._commonService.handleErrorDownload(error),
        })
      );
  }

  getVehicleUsageData$(
    tenantId: string,
    params?: any
  ): Observable<VehicleUsageDataResponse> {
    return this._httpClient.get<VehicleUsageDataResponse>(
      `${VECTORE_API_BASE_URL}/${tenantId}/vehicle-usage-data`,
      { params: this._commonService.buildParams(params) }
    );
  }
}
